import { BellOutlined, SyncOutlined, LogoutOutlined } from '@ant-design/icons';
import { Icon } from '@iconify/react';
import { Badge as AntdBadge, theme } from 'antd';
import { useState, ReactNode, useEffect, useRef } from 'react';

import { Avatar } from 'components/data-display/Avatar';
import { Popover } from 'components/data-display/Popover';
// TODO: (ENG-2520) Re-introduce theme toggle once dark-theme is integrated
// import { useTheme } from 'theme/MaximorThemeProvider';

import { Button } from 'components/general/Button';

import { useGetUnreadInboxNotifications } from 'hooks/inbox-notifications/useInboxNotifications';
import { useNotification } from 'hooks/utils/useNotification';
import { useScreenSize } from 'hooks/utils/useScreenSize';
import { useSessionManager } from 'hooks/utils/useSessionManager';

import { useAuthStore, useSidenavCollapseStore } from 'store/index';

import {
  InboxNotificationsDrawer,
  type NotificationTabType,
} from 'layout/inbox-notifications/InboxNotificationDrawer';
import { unreadNotificationsColor } from 'layout/inbox-notifications/_notificationUtils';
import { SearchCommand } from 'layout/search/SearchCommand';
import { searchRoutes } from 'layout/search/searchRoutes';

export function Topbar({ children }: { children?: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationActiveTab, setNotificationActiveTab] = useState<
    NotificationTabType | false
  >(false);
  const { setIsSidenavCollapsed } = useSidenavCollapseStore();
  const { isMobile } = useScreenSize();
  // TODO: (ENG-2520) Re-introduce theme toggle once dark-theme is integrated
  // const { toggleTheme } = useTheme();
  const { token } = theme.useToken();
  const notification = useNotification();
  const {
    data: notifications,
    isLoading: isLoadingNotifications,
    isError: isErrorNotifications,
  } = useGetUnreadInboxNotifications();

  const { logout } = useSessionManager();

  // Track previous notification count for background refetch toast
  const prevNotificationCountRef = useRef<number | null>(null);
  const unreadCount = notifications?.notifications.length ?? 0;

  // Handle error notifications for unread notifications
  useEffect(() => {
    if (isErrorNotifications) {
      notification.error({
        title: 'Load Failed',
        description: 'Failed to load notifications',
      });
    }
  }, [isErrorNotifications, notification]);

  // Show toast when new notifications arrive during background refetch (drawer closed)
  useEffect(() => {
    if (isLoadingNotifications) return;
    const prevCount = prevNotificationCountRef.current;
    const hasNewNotifications = prevCount !== null && unreadCount > prevCount;
    if (hasNewNotifications && !notificationActiveTab) {
      const newCount = unreadCount - prevCount;
      const notificationKey = `new-notifications-${Date.now()}`;
      notification.info({
        key: notificationKey,
        placement: 'topRight',
        icon: <BellOutlined />,
        title: 'New Notifications',
        description: (
          <span>
            You have{' '}
            <a
              className="underline cursor-pointer"
              onClick={() => {
                notification.destroy(notificationKey);
                setNotificationActiveTab('unread');
              }}
            >
              {newCount} new notification{newCount > 1 ? 's' : ''}
            </a>
          </span>
        ),
      });
    }
    prevNotificationCountRef.current = unreadCount;
  }, [
    unreadCount,
    isLoadingNotifications,
    notificationActiveTab,
    notification,
  ]);

  return (
    <div
      className="flex gap-2 justify-between p-3 px-4 h-16"
      style={{
        borderBottom: `1px solid ${token.colorBorder}`,
      }}
    >
      <div className="flex gap-1 items-center min-w-0">
        {isMobile && (
          <Button
            onClick={() => setIsSidenavCollapsed(false)}
            icon={<Icon icon="ant-design:menu-outlined" className="w-4 h-4" />}
          />
        )}
        {children ? children : <div className="" />}
      </div>
      <div className="flex items-center shrink-0">
        {/* TODO: (ENG-2520) Re-introduce theme toggle once dark-theme is integrated */}
        {/* <Button
          icon={<Icon icon="solar:sun-bold" />}
          onClick={toggleTheme}
          className="mr-3"
        /> */}

        <div className="flex items-center">
          <Button
            variant="outlined"
            onClick={() => setSearchOpen(true)}
            icon={<Icon icon="solar:command-linear" />}
            className="mr-3"
          >
            K
          </Button>
          <SearchCommand
            routes={searchRoutes}
            open={searchOpen}
            setOpen={setSearchOpen}
          />
        </div>

        <Button
          color="default"
          variant="text"
          className="mr-3"
          icon={
            <AntdBadge
              size="small"
              color={unreadNotificationsColor}
              overflowCount={9}
              count={
                isLoadingNotifications ? (
                  <SyncOutlined spin />
                ) : (
                  (notifications?.notifications.length ?? 0)
                )
              }
            >
              <BellOutlined style={{ fontSize: 16 }} />
            </AntdBadge>
          }
          onClick={() => setNotificationActiveTab('unread')}
        />
        <InboxNotificationsDrawer
          loading={isLoadingNotifications}
          open={!!notificationActiveTab}
          onClose={() => setNotificationActiveTab(false)}
          unreadNotifications={notifications?.notifications || []}
          activeTab={notificationActiveTab || 'unread'}
          onTabChange={setNotificationActiveTab}
        />

        <Popover
          content={
            <Button
              icon={<LogoutOutlined className="w-4 h-4" />}
              onClick={logout}
              variant="outlined"
              className="m-1"
              data-testid="logout-button"
            >
              Logout
            </Button>
          }
          trigger="click"
        >
          <div className="flex items-center gap-2" data-testid="user-account">
            <Avatar size={32} src={user?.photoURL}>
              {user?.displayName?.charAt(0)}
            </Avatar>
            <div className="flex flex-col">
              <p className="text-sm font-medium">{user?.displayName}</p>
            </div>
          </div>
        </Popover>
      </div>
    </div>
  );
}
